import moment from 'moment-timezone';
import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {oldActionPrioritiesById, TicketChangeHistory, TicketComment, TicketsService, TicketType} from '@app/shared/services/api/tickets';
import {Equipment} from '@app/graphql-typings/graphql';
import {SiteInfo} from '@shared/services/api/access-sites.interface';
import {User} from '@app/layout/admin/services/api/user-interfaces';
import {Impact, Priority} from '@app/layout/rules/template-editor/api/template';
import {ImpactId, PriorityId} from '@app/shared/api/model/type-ids';
import {TemplatesService} from '@app/layout/rules/template-editor/services/templates.service';
import {CollectionUtils} from '@app/shared/util/collections/collection-utils';
import {finalize, Subject, takeUntil} from 'rxjs';


type Activity = TicketComment | TicketChangeHistory;


@Component({
  selector: 'app-view-ticket-activity',
  templateUrl: './view-ticket-activity.component.html',
  styleUrls: ['./view-ticket-activity.component.scss'],
  standalone: false
})
export class ViewTicketActivityComponent implements OnInit, OnChanges, OnDestroy {
  private readonly ngUnsubscribe = new Subject<void>();

  @Input() ticketType: TicketType;
  @Input() comments: TicketComment[];
  @Input() allCommentsLoaded = false;
  @Input() changeHistory: TicketChangeHistory[];
  @Input() allChangeHistoryLoaded = false;
  @Input() showChangeHistory = false;
  @Input() site: SiteInfo;
  @Input() equipmentLoading = false;
  @Input() equipmentById: Map<number, Equipment>;
  @Input() assigneesByEmail: Map<string, User>;
  @Input() readOnly = false;
  @Output() editComment: EventEmitter<string> = new EventEmitter<string>();

  readonly ACTION_PRIORITY_UPGRADE_START_TS = moment('2025-03-02T00:11:00Z');
  readonly ACTION_PRIORITY_UPGRADE_END_TS = moment('2025-03-02T00:29:00Z');
  readonly oldActionPrioritiesById = oldActionPrioritiesById;

  impactsLoading = false;
  impactsById: Map<ImpactId, Impact>;
  prioritiesLoading = false;
  prioritiesById: Map<PriorityId, Priority>;
  activities: Activity[];

  constructor(private ticketsService: TicketsService,
              private templateService: TemplatesService) {
  }


  ngOnInit(): void {
    if (this.allCommentsLoaded == null) {
      throw new Error('allCommentsLoaded must be set');
    }
    if (this.allChangeHistoryLoaded == null) {
      throw new Error('allChangeHistoryLoaded must be set');
    }
    this.impactsLoading = true;
    this.templateService.getImpacts()
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => this.impactsLoading = false))
      .subscribe((impacts: Impact[]) => this.impactsById = CollectionUtils.indexBy(impacts, 'impact_id'));
    this.prioritiesLoading = true;
    this.templateService.getPriorities()
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => this.prioritiesLoading = false))
    .subscribe(priorities => this.prioritiesById = CollectionUtils.indexBy(priorities, 'priority_id'));
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      if (changes.allCommentsLoaded) {
        if (this.allCommentsLoaded == null) {
          throw new Error('allCommentsLoaded cannot be set to null or undefined');
        }
      }
      if (changes.allChangeHistoryLoaded) {
        if (this.allChangeHistoryLoaded == null) {
          throw new Error('allChangeHistoryLoaded cannot be set to null or undefined');
        }
      }
      if (changes.comments || changes.changeHistory || changes.showChangeHistory || changes.allCommentsLoaded || changes.allChangeHistoryLoaded) {
        if (this.showChangeHistory && this.changeHistory?.length) {
          // We are combining 2 streams (comments and change history) which are fetched separately based on number of
          // records to fetch from each (i.e. not fetching all records created before a certain timestamp). Therefore,
          // we cannot be sure that item(s) at the end ("end" is visual, i.e. earliest timestamp based on flow of time)
          // of the combined stream should not have items from the other stream shown above them.
          //
          // For example, if we are showing comments and change history, and we have fetched 10 records from each, and
          // say the last record ("last" is in the order the records are shown, i.e. bottom-most) in the combined stream
          // is a change history record, we cannot be sure that there are no comments between this last change history
          // record and the last comment we fetched. We simply could have not fetched those comments yet. Of course, if
          // all comments have already been fetched, then there is no problem.
          //
          // Therefore, unless we fetched all records from another stream, we need to not show any record that would be
          // shown below the bottom-most record of the other type that we have already fetched.
          //
          // In the above example, as the bottom-most record in the combined stream is change history, we need to work
          // out the timestamp of the bottom-most comment that was fetched, and not display any change history records
          // that would otherwise be shown below this comment.
          // If the bottom-most record in the combined stream was a comment, and not all change history records have
          // been fetched, we would not show the comments below the bottom-most change history record.

          // First, filter change history information that should not be shown as it is not "interesting" to users
          const changeHistory = this.changeHistory.reduce((acc, ch) => {
            const transformedChangeHistory = this.ticketsService.transformToViewableChangeHistoryRecord(this.ticketType, ch);
            if (transformedChangeHistory) {
              acc.push(transformedChangeHistory);
            }
            return acc;
          }, <TicketChangeHistory[]>[]);

          const earliestCommentTs = this.comments?.length ? this.comments[this.comments.length - 1].created_at : null;
          const earliestChangeHistoryTs = changeHistory?.length ? changeHistory[changeHistory.length - 1].ts : null;
          let commentTsBoundary: moment.Moment = null;
          let changeHistoryTsBoundary: moment.Moment = null;
          if (!this.allChangeHistoryLoaded && earliestCommentTs && earliestCommentTs.isBefore(earliestChangeHistoryTs)) {
            commentTsBoundary = earliestChangeHistoryTs;
          } else if (!this.allCommentsLoaded && earliestChangeHistoryTs && earliestChangeHistoryTs.isBefore(earliestCommentTs)) {
            changeHistoryTsBoundary = earliestCommentTs;
          }

          const activities: Activity[] = [];
          for (const comment of (this.comments || [])) {
            if (!commentTsBoundary || !comment.created_at.isBefore(commentTsBoundary)) {
              activities.push(comment);
            } else {
              break;
            }
          }
          for (const ch of (changeHistory || [])) {
            if (!changeHistoryTsBoundary || !ch.ts.isBefore(changeHistoryTsBoundary)) {
              activities.push(ch);
            } else {
              break;
            }
          }
          this.activities = activities;
        } else {
          this.activities = [...(this.comments || [])];
        }
        this.activities.sort(this.sortActivities.bind(this));
      }
    }
  }

  onEdit(commentId: string) {
    this.editComment.next(commentId);
  }

  isComment(activity: Activity): activity is TicketComment {
    return activity['comment_id'] !== undefined;
  }

  isChangeHistory(activity: Activity): activity is TicketChangeHistory {
    return activity['ticket_change_history_id'] !== undefined;
  }

  private sortActivities(a: Activity, b: Activity): number {
    const aTs: moment.Moment = this.isComment(a) ? a.created_at : a.ts;
    const bTs: moment.Moment = this.isComment(b) ? b.created_at : b.ts;
    return bTs.valueOf() - aTs.valueOf();
  }
}
